import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { BookOpen, Loader2, Pencil, Plus, Settings2, Trash2 } from 'lucide-react';

export type TextBausteinKategorie = 'vortext' | 'schlusstext' | 'frei';

export interface TextBaustein {
  id: string;
  name: string;
  kategorie: TextBausteinKategorie;
  text: string;
  beispiel?: boolean;
  createdAt: string;
  updatedAt: string;
}

const KATEGORIE_LABEL: Record<TextBausteinKategorie, string> = {
  vortext: 'Vortext',
  schlusstext: 'Schlusstext',
  frei: 'Frei',
};

const KATEGORIE_OPTIONS: TextBausteinKategorie[] = ['vortext', 'schlusstext', 'frei'];

async function fetchBausteine(): Promise<TextBaustein[]> {
  const res = await apiRequest('GET', '/api/text-bausteine');
  const json = await res.json();
  return (json?.data ?? []) as TextBaustein[];
}

interface TextBausteinFormState {
  id: string | null;
  name: string;
  kategorie: TextBausteinKategorie;
  text: string;
}

const EMPTY_FORM: TextBausteinFormState = { id: null, name: '', kategorie: 'frei', text: '' };

interface TextBausteinPickerProps {
  /** Ziel-Kategorie des Feldes, in das eingefügt wird. Kategorie 'frei' ist immer zusätzlich sichtbar. */
  kategorie: TextBausteinKategorie;
  /** Aktueller Wert des Ziel-Textfeldes, für das Anhängen mit Leerzeile. */
  currentValue: string;
  /** Wird mit dem neuen (zusammengeführten) Feldwert aufgerufen. */
  onInsert: (nextValue: string) => void;
  label?: string;
  className?: string;
}

export function TextBausteinPicker({
  kategorie,
  currentValue,
  onInsert,
  label = 'Textbaustein einfügen',
  className,
}: TextBausteinPickerProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState<TextBausteinFormState>(EMPTY_FORM);

  const { data: bausteine = [], isLoading } = useQuery({
    queryKey: ['/api/text-bausteine'],
    queryFn: fetchBausteine,
  });

  const filtered = useMemo(
    () => bausteine.filter((b) => b.kategorie === kategorie || b.kategorie === 'frei'),
    [bausteine, kategorie],
  );

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['/api/text-bausteine'] });

  const createMutation = useMutation({
    mutationFn: async (payload: { name: string; kategorie: TextBausteinKategorie; text: string }) => {
      const res = await apiRequest('POST', '/api/text-bausteine', payload);
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      setForm(EMPTY_FORM);
      toast({ title: 'Textbaustein angelegt' });
    },
    onError: (error: Error) => {
      toast({ title: 'Fehler beim Anlegen', description: error.message, variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: { id: string; name: string; kategorie: TextBausteinKategorie; text: string }) => {
      const { id, ...body } = payload;
      const res = await apiRequest('PUT', `/api/text-bausteine/${id}`, body);
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      setForm(EMPTY_FORM);
      toast({ title: 'Textbaustein gespeichert' });
    },
    onError: (error: Error) => {
      toast({ title: 'Fehler beim Speichern', description: error.message, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest('DELETE', `/api/text-bausteine/${id}`);
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      toast({ title: 'Textbaustein gelöscht' });
    },
    onError: (error: Error) => {
      toast({ title: 'Fehler beim Löschen', description: error.message, variant: 'destructive' });
    },
  });

  const handleSelect = (baustein: TextBaustein) => {
    const merged = currentValue?.trim() ? `${currentValue}\n\n${baustein.text}` : baustein.text;
    onInsert(merged);
    setPopoverOpen(false);
  };

  const handleSubmitForm = () => {
    if (!form.name.trim() || !form.text.trim()) {
      toast({ title: 'Name und Text sind erforderlich', variant: 'destructive' });
      return;
    }
    if (form.id) {
      updateMutation.mutate({ id: form.id, name: form.name.trim(), kategorie: form.kategorie, text: form.text.trim() });
    } else {
      createMutation.mutate({ name: form.name.trim(), kategorie: form.kategorie, text: form.text.trim() });
    }
  };

  const startEdit = (baustein: TextBaustein) => {
    setForm({ id: baustein.id, name: baustein.name, kategorie: baustein.kategorie, text: baustein.text });
  };

  const handleDelete = (baustein: TextBaustein) => {
    if (window.confirm(`Textbaustein "${baustein.name}" wirklich löschen?`)) {
      deleteMutation.mutate(baustein.id);
    }
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" size="sm" className={className}>
            <BookOpen className="h-4 w-4 mr-1.5" />
            {label}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0" align="start">
          <Command>
            <CommandInput
              placeholder="Textbaustein suchen..."
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              {isLoading ? (
                <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Lädt...
                </div>
              ) : (
                <>
                  <CommandEmpty>Keine Textbausteine gefunden.</CommandEmpty>
                  <CommandGroup>
                    {filtered.map((baustein) => (
                      <CommandItem
                        key={baustein.id}
                        value={`${baustein.name} ${baustein.text}`}
                        onSelect={() => handleSelect(baustein)}
                        className="flex flex-col items-start gap-0.5"
                      >
                        <div className="flex items-center gap-2 w-full">
                          <span className="font-medium">{baustein.name}</span>
                          <span className="text-xs text-muted-foreground ml-auto">
                            {KATEGORIE_LABEL[baustein.kategorie]}
                          </span>
                        </div>
                        <span className="text-xs text-muted-foreground line-clamp-1">{baustein.text}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
            <div className="border-t p-1.5">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full justify-start text-muted-foreground"
                onClick={() => {
                  setPopoverOpen(false);
                  setForm({ ...EMPTY_FORM, kategorie });
                  setManageOpen(true);
                }}
              >
                <Settings2 className="h-4 w-4 mr-1.5" />
                Textbausteine verwalten...
              </Button>
            </div>
          </Command>
        </PopoverContent>
      </Popover>

      <Dialog
        open={manageOpen}
        onOpenChange={(open) => {
          setManageOpen(open);
          if (!open) setForm(EMPTY_FORM);
        }}
      >
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Textbausteine verwalten</DialogTitle>
            <DialogDescription>
              Vortexte, Schlusstexte und freie Textbausteine für Angebote und Rechnungen anlegen, bearbeiten und löschen.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-md border p-3 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <Label htmlFor="baustein-name">Name</Label>
                  <Input
                    id="baustein-name"
                    value={form.name}
                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="z.B. VOB/B Vortext Tiefbau"
                  />
                </div>
                <div>
                  <Label htmlFor="baustein-kategorie">Kategorie</Label>
                  <Select
                    value={form.kategorie}
                    onValueChange={(value) => setForm((prev) => ({ ...prev, kategorie: value as TextBausteinKategorie }))}
                  >
                    <SelectTrigger id="baustein-kategorie">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {KATEGORIE_OPTIONS.map((option) => (
                        <SelectItem key={option} value={option}>
                          {KATEGORIE_LABEL[option]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="baustein-text">Text</Label>
                <Textarea
                  id="baustein-text"
                  value={form.text}
                  onChange={(e) => setForm((prev) => ({ ...prev, text: e.target.value }))}
                  rows={4}
                  placeholder="Textbaustein-Inhalt..."
                />
              </div>
              <div className="flex justify-end gap-2">
                {form.id && (
                  <Button type="button" variant="outline" size="sm" onClick={() => setForm(EMPTY_FORM)}>
                    Abbrechen
                  </Button>
                )}
                <Button type="button" size="sm" onClick={handleSubmitForm} disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                  <Plus className="h-4 w-4 mr-1.5" />
                  {form.id ? 'Speichern' : 'Anlegen'}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              {bausteine.length === 0 && (
                <p className="text-sm text-muted-foreground">Noch keine Textbausteine vorhanden.</p>
              )}
              {bausteine.map((baustein) => (
                <div
                  key={baustein.id}
                  className="flex items-start justify-between gap-3 rounded-md border p-2.5"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{baustein.name}</span>
                      <span className="text-xs text-muted-foreground">{KATEGORIE_LABEL[baustein.kategorie]}</span>
                      {baustein.beispiel && (
                        <span className="text-xs text-muted-foreground italic">Beispiel</span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">{baustein.text}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button type="button" variant="ghost" size="icon" onClick={() => startEdit(baustein)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(baustein)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setManageOpen(false)}>
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default TextBausteinPicker;
